import { FormControl, Grid, InputLabel } from '@mui/material';
import { useAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';

import DynamicFormControl from '@/components/DynamicFormControl';
import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';
import { selectedSourceTypeIdAtom } from '@/features/SourceCreate/SourceCreate.store';
import type { DynamicFieldData } from '@/features/SourceCreate/SourceCreate.types';

import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import type { CategorySelectorProps } from './CategorySelector.types';

/**
 * CategorySelector renders a multi-select for choosing log categories.
 * Categories can be mandatory or optional depending on source type configuration.
 * Uses DynamicFormControl autocomplete type to avoid code duplication.
 * Not shown for Google Workspace (which uses hierarchical categories instead).
 */
const CategorySelector = ({
  connectionInputSchema,
  categoryMasterList,
  categories,
  onCategoryChange,
  isResettingSourceType
}: CategorySelectorProps) => {
  const { formState } = useFormContext();
  const { errors } = formState;
  const [selectedSourceTypeId] = useAtom(selectedSourceTypeIdAtom);

  if (isResettingSourceType) return null;
  if (!connectionInputSchema?.show_categories) return null;
  if (selectedSourceTypeId === SOURCE_TYPE.GOOGLE_WORKSPACE) return null;

  const hasError = Boolean(errors?.category);
  const errorMessage = errors?.category?.message as string;
  const isMandatory = connectionInputSchema?.mandatory_categories;

  const showSelectAll =
    connectionInputSchema?.show_select_all_in_categories ??
    (selectedSourceTypeId !== SOURCE_TYPE.O365 &&
      selectedSourceTypeId !== SOURCE_TYPE.MICROSOFT_DEFENDER &&
      selectedSourceTypeId !== SOURCE_TYPE.MICROSOFT_ENTRA_ID &&
      selectedSourceTypeId !== SOURCE_TYPE.MICROSOFT);

  const availableCategories = categoryMasterList.map((option) => option.name);

  const categoryFieldConfig: DynamicFieldData = {
    id: 0,
    name: 'category',
    type: 'autocomplete',
    label: 'Category',
    value: categories || [],
    validations: [],
    validation_type: '',
    options: availableCategories,
    showSelectAll,
    onFieldChange: (value: unknown[]) => onCategoryChange(value as string[]),
    config: {
      validate: (selected) => {
        if (!isMandatory) return true;
        if (!selected || selected.length === 0) {
          return 'Select Category';
        }
        return true;
      }
    }
  };

  return (
    <Grid size={{ xs: 12, sm: 12, md: 8 }}>
      <InputLabel htmlFor="category-input">
        Category
        {isMandatory && <span> *</span>}
      </InputLabel>
      <FormControl fullWidth>
        <DynamicFormControl {...categoryFieldConfig} />
        {hasError && <ErrorMessage message={errorMessage} />}
      </FormControl>
    </Grid>
  );
};

export default CategorySelector;
